import { useCallback, useMemo, useState } from 'react';

import groupIntoSectionHelper from '../schema/groupIntoSection';
import propertyToFieldDef from '../schema/propertyToFieldDef';
import slugify from '../schema/slugify';
import toFieldDefs from '../schema/toFieldDefs';
import toJsonSchema from '../schema/toJsonSchema';
import toSectionMetas from '../schema/toSectionMetas';
import {
  type FieldDef,
  type FormBuilderWidget,
  type FormSchema,
  type LibraryQuestion,
  type LibrarySection,
  type SectionMeta
} from '../types';

import { fieldTypeMeta } from './fieldTypes';

const uniqueKey = (base: string, taken: Set<string>) => {
  let key = base;
  let n = 2;
  while (taken.has(key)) key = `${base}_${n++}`;
  return key;
};

const blankField = (widget: FormBuilderWidget, taken: Set<string>): FieldDef => {
  const meta = fieldTypeMeta(widget);
  const key = uniqueKey(slugify(meta.label), taken);
  const field: FieldDef = { key, label: meta.label, widget, required: false };
  if (widget === 'select' || widget === 'radio') {
    field.options = [
      { value: 'option_1', label: 'Option 1' },
      { value: 'option_2', label: 'Option 2' }
    ];
  }
  if (widget === 'autocomplete') field.model = 'staff';
  // Content widgets carry required config the backend validates. Seed a
  // placeholder body so an html_content field is valid the moment it lands;
  // an image still needs a real URL, supplied through the settings panel.
  if (widget === 'html_content') field.body = 'Text to read';
  return field;
};

// A field joining a section lands after that section's last member rather than
// at the end of the form, so the schema's document order matches what the canvas
// shows — the backend files each field into the section it names, in the order
// the schema lists it, so appending would sink the field below later sections.
const placeField = (fields: FieldDef[], field: FieldDef): FieldDef[] => {
  if (!field.sectionKey) return [...fields, field];
  const last = fields.reduce((at, f, i) => (f.sectionKey === field.sectionKey ? i : at), -1);
  if (last === -1) return [...fields, field];
  const next = [...fields];
  next.splice(last + 1, 0, field);
  return next;
};

export interface BuilderState {
  fields: FieldDef[];
  sections: SectionMeta[];
  selectedFieldKey: string | null;
  // The section new fields and inserted questions join, or null for the form
  // body. Never a section linked to the library, whose members are its master's.
  activeSectionKey: string | null;
  checkedKeys: Set<string>;
  schema: FormSchema;
  isDirty: boolean;

  select: (key: string | null) => void;
  // Aims the library panel at a section. Ignored for a linked section, which
  // can't take a field of the form's own.
  targetSection: (key: string | null) => void;
  toggleChecked: (key: string) => void;
  clearChecked: () => void;

  addField: (widget: FormBuilderWidget, sectionKey?: string) => void;
  // False when the question's key is already on the form, which is the one case
  // a linked insert can't be honoured.
  insertQuestion: (question: LibraryQuestion, sectionKey?: string) => boolean;
  // False when one of the section's members is already on the form.
  insertSection: (section: LibrarySection) => boolean;
  updateField: (key: string, patch: Partial<FieldDef>) => void;
  removeField: (key: string) => void;
  reorderFields: (activeKey: string, overKey: string) => void;

  groupChecked: (title: string) => void;
  renameSection: (key: string, title: string) => void;
  linkSection: (key: string, sectionId: string) => void;
  unlinkSection: (key: string) => void;
  removeSection: (key: string) => void;

  markSaved: () => void;
}

const useBuilderState = (initialSchema: FormSchema | null | undefined): BuilderState => {
  const [fields, setFields] = useState<FieldDef[]>(() => toFieldDefs(initialSchema));
  const [sections, setSections] = useState<SectionMeta[]>(() => toSectionMetas(initialSchema));
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null);
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);

  const isLibrarySection = useCallback(
    (key?: string | null) => !!sections.find((s) => s.key === key)?.sectionId,
    [sections]
  );

  // One gate for the whole builder: a linked section can never become the
  // target, so addField and insertQuestion can trust the section they're given.
  const targetSection = useCallback(
    (key: string | null) => {
      if (key !== null && isLibrarySection(key)) return;
      setActiveSectionKey(key);
    },
    [isLibrarySection]
  );

  const takenKeys = useCallback(() => new Set(fields.map((f) => f.key)), [fields]);

  const mutate = useCallback((next: FieldDef[], nextSections?: SectionMeta[]) => {
    setFields(next);
    if (nextSections) setSections(nextSections);
    setDirty(true);
  }, []);

  const addField = useCallback(
    (widget: FormBuilderWidget, sectionKey?: string) => {
      // A field of the form's own can live in an ad-hoc section: that section
      // owns its members, so the backend mints the field a private question and
      // files it there. A linked section is refused, not silently unsectioned.
      if (sectionKey && isLibrarySection(sectionKey)) return;
      const field = blankField(widget, takenKeys());
      if (sectionKey) field.sectionKey = sectionKey;
      mutate(placeField(fields, field));
      setSelectedFieldKey(field.key);
    },
    [fields, mutate, takenKeys, isLibrarySection]
  );

  const insertQuestion = useCallback(
    (question: LibraryQuestion, sectionKey?: string) => {
      // Questions insert linked: the field keeps the library question's id, so
      // answers roll up with every other form asking it and a master edit flows
      // through. That link is keyed on the question's own key, which the form
      // therefore can't rename or hold twice — so an insert onto a key already
      // in use is refused rather than quietly turned into a copy.
      if (takenKeys().has(question.key)) return false;
      // Even a library question can't be added to a linked section: the section
      // decides its own membership, so the insert would be dropped on rebuild.
      if (sectionKey && isLibrarySection(sectionKey)) return false;
      const field = propertyToFieldDef(question.key, question.definition, false);
      field.questionId = question.id;
      field.inLibrary = true;
      if (sectionKey) field.sectionKey = sectionKey;
      mutate(placeField(fields, field));
      setSelectedFieldKey(field.key);
      return true;
    },
    [fields, mutate, takenKeys, isLibrarySection]
  );

  const insertSection = useCallback(
    (section: LibrarySection) => {
      // Members belong to the master, which resolves them by their own keys on
      // every load — renaming one here would not survive the round trip, so a
      // section whose members clash with the form is refused instead.
      const taken = takenKeys();
      if (section.questions.some((member) => taken.has(member.key))) return false;
      const sectionKey = uniqueKey(section.key, new Set(sections.map((s) => s.key)));
      const members: FieldDef[] = section.questions.map((member) => {
        const field = propertyToFieldDef(member.key, member.definition, member.required);
        // Membership is the proof: a section only holds library questions, so
        // every member shows as linked whether or not its definition says so.
        field.inLibrary = true;
        field.sectionKey = sectionKey;
        return field;
      });
      // Linked section: carry its sectionId so edits to the master flow through.
      mutate(
        [...fields, ...members],
        [...sections, { key: sectionKey, title: section.title, sectionId: section.id }]
      );
      return true;
    },
    [fields, sections, mutate, takenKeys]
  );

  const updateField = useCallback(
    (key: string, patch: Partial<FieldDef>) => {
      const index = fields.findIndex((f) => f.key === key);
      if (index === -1) return;
      // Keys are the field's identity, so a rename onto a key another field
      // already holds would make the two indistinguishable — every later edit
      // would patch both at once. Drop the rename and keep the rest of the patch.
      const collides =
        patch.key !== undefined && fields.some((f, i) => i !== index && f.key === patch.key);
      const applied = { ...patch };
      if (collides) delete applied.key;
      mutate(fields.map((f, i) => (i === index ? { ...f, ...applied } : f)));
      if (collides) return;
      // The key doubles as the field's identity (selection, checked state,
      // React keys). Renaming it must carry that identity across, or the
      // panel loses the field mid-edit and the input blurs.
      if (patch.key && patch.key !== key) {
        const renamed = patch.key;
        setSelectedFieldKey((current) => (current === key ? renamed : current));
        setCheckedKeys((current) => {
          if (!current.has(key)) return current;
          const next = new Set(current);
          next.delete(key);
          next.add(renamed);
          return next;
        });
      }
    },
    [fields, mutate]
  );

  const removeField = useCallback(
    (key: string) => {
      mutate(fields.filter((f) => f.key !== key));
      setSelectedFieldKey((current) => (current === key ? null : current));
      setCheckedKeys((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    },
    [fields, mutate]
  );

  const reorderFields = useCallback(
    (activeKey: string, overKey: string) => {
      const from = fields.findIndex((f) => f.key === activeKey);
      const to = fields.findIndex((f) => f.key === overKey);
      if (from === -1 || to === -1 || from === to) return;
      // A linked section's fields are its master's, so a field from elsewhere
      // has nowhere to be stored there and the drop is refused.
      const target = fields[to].sectionKey;
      if (target !== fields[from].sectionKey && isLibrarySection(target)) return;
      const next = [...fields];
      const [moved] = next.splice(from, 1);
      // A field dropped onto another adopts that field's section membership.
      moved.sectionKey = target;
      next.splice(to, 0, moved);
      mutate(next);
    },
    [fields, mutate, isLibrarySection]
  );

  const groupChecked = useCallback(
    (title: string) => {
      if (checkedKeys.size === 0) return;
      const sectionKey = uniqueKey(slugify(title, 'section'), new Set(sections.map((s) => s.key)));
      mutate(groupIntoSectionHelper(fields, checkedKeys, sectionKey), [
        ...sections,
        { key: sectionKey, title }
      ]);
      setCheckedKeys(new Set());
      // Grouping is usually the first half of building a section out, so aim
      // the library at the section that was just made.
      setActiveSectionKey(sectionKey);
    },
    [fields, sections, checkedKeys, mutate]
  );

  const renameSection = useCallback((key: string, title: string) => {
    setSections((current) => current.map((s) => (s.key === key ? { ...s, title } : s)));
    setDirty(true);
  }, []);

  const linkSection = useCallback((key: string, sectionId: string) => {
    setSections((current) => current.map((s) => (s.key === key ? { ...s, sectionId } : s)));
    // The master owns its members now, so the section can no longer take a
    // field of the form's own and must not stay the library's target.
    setActiveSectionKey((current) => (current === key ? null : current));
    setDirty(true);
  }, []);

  const unlinkSection = useCallback(
    (key: string) => {
      // Detach from the library master; members stay but lose their link so the
      // section becomes form-local and editable in place.
      setSections((current) =>
        current.map((s) => (s.key === key ? { ...s, sectionId: undefined } : s))
      );
      mutate(
        fields.map((f) =>
          f.sectionKey === key ? { ...f, questionId: undefined, inLibrary: undefined } : f
        )
      );
    },
    [fields, mutate]
  );

  const removeSection = useCallback(
    (key: string) => {
      mutate(
        fields.filter((f) => f.sectionKey !== key),
        sections.filter((s) => s.key !== key)
      );
      setActiveSectionKey((current) => (current === key ? null : current));
    },
    [fields, sections, mutate]
  );

  const schema = useMemo(() => toJsonSchema(fields, sections), [fields, sections]);

  return {
    fields,
    sections,
    selectedFieldKey,
    activeSectionKey,
    checkedKeys,
    schema,
    isDirty: dirty,
    select: setSelectedFieldKey,
    targetSection,
    toggleChecked: (key) =>
      setCheckedKeys((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    clearChecked: () => setCheckedKeys(new Set()),
    addField,
    insertQuestion,
    insertSection,
    updateField,
    removeField,
    reorderFields,
    groupChecked,
    renameSection,
    linkSection,
    unlinkSection,
    removeSection,
    markSaved: () => setDirty(false)
  };
};

export default useBuilderState;
